import React, { useMemo, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IApiQueryOptions } from 'api/ApiClient';
import { FilterButton, ModalFactory, Section, SectionHeader, StatusCheckbox, UserTable } from 'components';
import { TableHeaderObject } from 'components/Table/User/UserTable';
import { ConfirmResendInvite } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { Employee, resendInvitationRequest } from 'store/Employee';
import { selectIsPermitted } from 'store/Employers';
import { IStore } from 'store/types';
import { ApiQueryOperator, EmployeeActionRequiredType, EmployeeRole, EmployeesTab, EmployeeStatus, IApiQueryParam, IEmployee } from 'types';
import { ACTION_REQUIRED_FILTERS, ACTION_REQUIRED_TYPES, ALL_STATUSES, DEFAULT_OFFSET, EXPIRED_STATUS_FILTERS, INVITE_FAILURE_STATUS_FILTERS, PENDING_STATUS_FILTERS, updateStatusFilters } from 'utils';
import ReviewUserScreen from './ReviewUser';
import UsersFilters from './UsersFilters';

export const TABLE_HEADERS_ALL: TableHeaderObject[] = [
  { label: 'Name', value: 'name', sortable: true },
  { label: 'Site', value: 'sites', sortable: false },
  { label: 'Role', value: 'role', sortable: false },
  { label: 'Job Title', value: 'jobTitle', sortable: false },
  { label: 'Status', value: 'status', sortable: false },
];

export const TABLE_HEADERS_REVIEW: TableHeaderObject[] = [
  { label: 'Name', value: 'name', sortable: true },
  { label: 'Site', value: 'sites', sortable: false },
  { label: 'Role', value: 'role', sortable: false },
  { label: 'Job Title', value: 'jobTitle', sortable: false },
  { label: 'Invite Method', value: 'inviteRecipient', align: 'left' },
  { label: 'Status', value: 'status', sortable: false },
  { label: '', value: 'reviewAction' },
];

const reviewStatusOptions = [
  { value: ACTION_REQUIRED_FILTERS, label: 'All' },
  { value: PENDING_STATUS_FILTERS, label: 'Pending' },
  { value: INVITE_FAILURE_STATUS_FILTERS, label: 'Invited - Delivery Failure' },
  { value: EXPIRED_STATUS_FILTERS, label: 'Expired' },
];

export interface IUsersTabProps {
  activeTab: EmployeesTab;
  currentSiteId: string;
  currentOrganizationId: string;
  employees: IEmployee[];
  filterActive: boolean;
  isLoading: boolean;
  totalEmployees: number;
  urlQuery: IApiQueryOptions;
  toggleFilterActive: (active: boolean) => void;
  onQueryChange: (query: IApiQueryOptions) => void;
  onConfirm?: () => void;
}

const UsersTab: React.FC<IUsersTabProps> = ({ activeTab, currentSiteId, currentOrganizationId, employees, filterActive, isLoading, totalEmployees, urlQuery, toggleFilterActive, onQueryChange, onConfirm }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const disableReviewButton = !useSelector(selectIsPermitted([EmployeeRole.SITE_ADMIN, EmployeeRole.ADMIN]));
  const isResendLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employee.UPDATE_EMPLOYEE_REQUEST));
  const [activeReviewEmployee, setActiveReviewEmployee] = useState<IEmployee | undefined>();

  const isReviewTab = activeTab === EmployeesTab.REVIEW;
  const tableHeaders = isReviewTab ? TABLE_HEADERS_REVIEW : TABLE_HEADERS_ALL;

  const filterMenuActiveCount = useMemo(
    () => (urlQuery?.q ?? []).filter((item) => ['name', 'type', 'sites.job.title'].includes(item.key) || (item.key === EmployeeStatus.ACTION_REQUIRED && ACTION_REQUIRED_TYPES.includes(item.value as EmployeeActionRequiredType))).length,
    [urlQuery?.q]
  );

  const checkedStatusLabels = useMemo((): string[] => {
    const statusLabels: string[] = [];
    const statusFilter = (urlQuery.q || []).find((q: IApiQueryParam) => q.key === 'status' && q.operator === ApiQueryOperator.in)?.value;
    const actionRequiredFilter = (urlQuery.q || []).find((q: IApiQueryParam) => q.key === 'action_required' && q.operator === ApiQueryOperator.in)?.value;

    if (statusFilter?.includes(EmployeeStatus.APPROVED)) statusLabels.push(EmployeeStatus.APPROVED);
    if (statusFilter?.includes(EmployeeStatus.DISMISSED)) statusLabels.push(EmployeeStatus.DISMISSED);
    if (statusFilter?.includes(EmployeeStatus.INVITED)) statusLabels.push(EmployeeStatus.INVITED);
    if (statusFilter?.includes(EmployeeStatus.ACTION_REQUIRED)) {
      if (actionRequiredFilter?.includes(EmployeeActionRequiredType.EMPLOYER_APPROVAL)) statusLabels.push('pending');
      if (actionRequiredFilter?.includes(EmployeeActionRequiredType.INVITE_EXPIRED)) statusLabels.push('expired');
    }

    return statusLabels;
  }, [urlQuery.q]);

  const onStatusChange = (newStatus: string[]) => {
    const updatedFilters = updateStatusFilters(newStatus, urlQuery.q);
    onQueryChange({ ...urlQuery, offset: DEFAULT_OFFSET, q: updatedFilters });
  };

  const [showReviewUserModal, hideReviewUserModal] = useModal(() => {
    const onClose = () => {
      setActiveReviewEmployee(undefined);
      hideReviewUserModal();
    };
    return <ReviewUserScreen employee={activeReviewEmployee} onClose={onClose} onDeny={onClose} onApprove={() => onConfirm?.()} visible={!!activeReviewEmployee} />;
  }, [activeReviewEmployee]);

  const [showConfirmResendInviteModal, hideConfirmResendInviteModal] = useModal(() => {
    const onClose = () => {
      setActiveReviewEmployee(undefined);
      hideConfirmResendInviteModal();
    };
    const onSuccess = () => {
      onConfirm?.();
      onClose();
    };
    return (
      <ModalFactory customModal isActive onClose={onClose}>
        <ConfirmResendInvite
          onClose={onClose}
          onResend={() => {
            if (activeReviewEmployee?.inviteRecipient) {
              dispatch(resendInvitationRequest(activeReviewEmployee.id, activeReviewEmployee.inviteRecipient, { onSuccess }));
            }
          }}
          isLoading={isResendLoading}
          employee={activeReviewEmployee}
        />
      </ModalFactory>
    );
  }, [activeReviewEmployee, isResendLoading, onConfirm]);

  return (
    <>
      <Section data-cy="table">
        <SectionHeader
          title="Users"
          value={`(${totalEmployees})`}
          isLoading={isLoading}
          filterActive={filterActive}
          staticSubtitle
          subtitle={!isReviewTab && <StatusCheckbox label="User Status:" statuses={ALL_STATUSES} activeStatus={checkedStatusLabels} onChange={onStatusChange} />}
        >
          <FilterButton filterActive={filterActive} appliedFilters={filterMenuActiveCount} onClick={() => toggleFilterActive(!filterActive)} />
        </SectionHeader>
        <UsersFilters
          isVisible={filterActive}
          data-cy="filter-section"
          roleVisible
          statusVisible={isReviewTab}
          statusOptions={reviewStatusOptions}
          jobTitleVisible
          siteId={currentSiteId}
          organizationId={currentOrganizationId}
          urlQuery={urlQuery}
          onFiltersChange={(query: IApiQueryParam[]) => onQueryChange({ ...urlQuery, q: query })}
        />
        <UserTable
          currentSiteId={currentSiteId}
          employees={employees}
          disableReviewButton={disableReviewButton}
          urlQuery={urlQuery}
          isLoading={isLoading}
          tableHeaders={tableHeaders}
          onEmployeeSelect={(employeeId: string) => history.push({ pathname: `/users/${employeeId}` })}
          onSortChange={(newSort: string) => onQueryChange({ ...urlQuery, sort: urlQuery.sort === newSort ? `-${urlQuery.sort}` : newSort })}
          {...(isReviewTab && {
            onReviewSelect: (employeeId: string) => {
              setActiveReviewEmployee(employees.find((employee) => employee.id === employeeId));
              showReviewUserModal();
            },
            onResendSelect: (employeeId: string) => {
              setActiveReviewEmployee(employees.find((employee) => employee.id === employeeId));
              showConfirmResendInviteModal();
            },
          })}
        />
      </Section>
    </>
  );
};

export default UsersTab;
